import { Link, type Href } from 'expo-router';
import type { ReactNode } from 'react';
import { Platform, StyleSheet } from 'react-native';

import { useTheme } from '@/hooks/use-theme';

// The one inline link style for running prose — the home page's SEO copy
// (main-content.tsx) and article bodies (portable-text-body.tsx) — so links
// look the same wherever text flows around them.
//
// Native gets a static gold underline. On web the decoration and the hover
// effect (underline firms up, a soft gold highlight sweeps in under the text)
// live in the `a[data-prose-link]` rule in app/+html.tsx: CSS is the only way
// to get :hover / :focus-visible and transitions on a react-native-web <Text>.
// The decoration is deliberately NOT set in the RN style on web, so that rule
// owns it outright.

// react-native-web turns `dataSet` into data-* attributes (the hook for the CSS
// rule); Link forwards it to its <Text>, but neither type declares it.
const WEB_ATTRS = { dataSet: { proseLink: '' } } as object;

/** Hosts whose absolute URLs are really internal links (Sanity authors paste full URLs). */
const SITE_HOST = /^https?:\/\/(www\.)?riviera-todo\.(com|fr)(?=\/|$)/i;

/**
 * Turns an authored href into a router path when it points at this site, so a
 * link in an article navigates client-side (and stays on localhost in dev)
 * instead of reloading the production origin. Trailing slashes are dropped to
 * match the canonical, slash-free URLs. Returns null for external links.
 */
export function internalPath(href: string): string | null {
  let path: string;
  if (SITE_HOST.test(href)) path = href.replace(SITE_HOST, '') || '/';
  else if (href.startsWith('/') && !href.startsWith('//')) path = href;
  else return null;
  return path.length > 1 ? path.replace(/\/+(?=$|[?#])/, '') : path;
}

export function ProseLink({
  href,
  external = false,
  children,
}: {
  href: Href;
  /** Opens in a new tab on web. */
  external?: boolean;
  children: ReactNode;
}) {
  const theme = useTheme();
  return (
    <Link
      href={href}
      {...WEB_ATTRS}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
      style={[
        styles.link,
        { color: theme.accentForeground },
        Platform.OS !== 'web' && [styles.nativeUnderline, { textDecorationColor: theme.gold }],
      ]}>
      {children}
    </Link>
  );
}

const styles = StyleSheet.create({
  link: {
    fontWeight: '600',
  },
  nativeUnderline: {
    textDecorationLine: 'underline',
  },
});
